import { useMemo, useState, type CSSProperties } from 'react'
import type { TagCategory, TrainingTagsGroupedDto, TrainingTagDto } from '../../api/trainingBlockApi'
import { useTranslation } from '../../i18n/useTranslation'
import { useTheme } from '../../hooks/useTheme'

interface TagSelectorProps {
  catalog: TrainingTagsGroupedDto
  categories: TagCategory[]
  selected: string[]
  onChange: (tags: string[]) => void
  /** Start with the full catalog open. Defaults to collapsed. */
  defaultExpanded?: boolean
}

const CATEGORY_TO_GROUP: Record<TagCategory, keyof TrainingTagsGroupedDto> = {
  muscle: 'muscles',
  level: 'levels',
  gender: 'genders',
  type: 'types',
}

export function TagSelector({
  catalog,
  categories,
  selected,
  onChange,
  defaultExpanded = false,
}: TagSelectorProps) {
  const theme = useTheme()
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(defaultExpanded)

  const flatCatalog = useMemo(
    () => categories.flatMap(category => catalog[CATEGORY_TO_GROUP[category]] ?? []),
    [catalog, categories],
  )

  const selectedTags = useMemo(
    () => flatCatalog.filter(tag => selected.includes(tag.key)),
    [flatCatalog, selected],
  )

  function toggle(key: string) {
    if (selected.includes(key)) {
      onChange(selected.filter(k => k !== key))
    } else {
      onChange([...selected, key])
    }
  }

  function removeTag(key: string) {
    onChange(selected.filter(k => k !== key))
  }

  function tagLabel(tag: TrainingTagDto): string {
    return t.trainingTags[tag.key as keyof typeof t.trainingTags] ?? tag.label
  }

  function chipStyle(active: boolean): CSSProperties {
    return active
      ? {
          backgroundColor: `${theme.buttonColor}20`,
          borderColor: `${theme.buttonColor}60`,
          color: theme.accent.text,
          fontWeight: 600,
        }
      : {
          backgroundColor: theme.isDark ? 'rgba(255,255,255,0.06)' : '#ffffff',
          borderColor: theme.cardBorder,
          color: theme.text.muted,
        }
  }

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{
        backgroundColor: theme.cardBg,
        border: `1px solid ${selected.length > 0 ? `${theme.buttonColor}35` : theme.cardBorder}`,
      }}
    >
      {/* Compact header */}
      <div className="flex items-start gap-2 px-3 py-2.5">
        <div className="flex-1 min-w-0">
          {selectedTags.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {selectedTags.map(tag => (
                <button
                  key={tag.key}
                  type="button"
                  onClick={() => (expanded ? toggle(tag.key) : removeTag(tag.key))}
                  className="flex items-center gap-1 min-h-[32px] pl-2.5 pr-2 rounded-full text-xs border transition-all active:scale-95"
                  style={chipStyle(true)}
                  title={expanded ? undefined : t.createTrainingBlock.tagsRemove}
                >
                  <span className="leading-none">{tag.emoji}</span>
                  <span>{tagLabel(tag)}</span>
                  {!expanded && (
                    <span className="opacity-60 ml-0.5 text-sm leading-none" aria-hidden>
                      ×
                    </span>
                  )}
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm py-1" style={{ color: theme.text.muted }}>
              {t.createTrainingBlock.tagsEmptyHint}
            </p>
          )}
        </div>

        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="flex items-center gap-1 shrink-0 min-h-[44px] px-2.5 rounded-xl text-xs font-semibold transition-opacity active:opacity-70"
          style={{
            color: theme.accent.text,
            backgroundColor: `${theme.buttonColor}12`,
          }}
          aria-expanded={expanded}
        >
          {expanded ? t.createTrainingBlock.tagsDone : t.createTrainingBlock.tagsAdd}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="transition-transform duration-200"
            style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
            aria-hidden
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      </div>

      {/* Full catalog when editing */}
      {expanded && (
        <div
          className="flex flex-col gap-3 px-3 pb-3.5 pt-2"
          style={{ borderTop: `1px solid ${theme.cardBorder}` }}
        >
          {categories.map(category => {
            const tags = catalog[CATEGORY_TO_GROUP[category]] ?? []
            if (tags.length === 0) return null
            return (
              <div key={category}>
                <p
                  className="text-[11px] font-medium mb-1.5 uppercase tracking-wide"
                  style={{ color: theme.text.muted }}
                >
                  {t.trainingTagSections[category] ?? category}
                </p>
                <div className="flex flex-wrap gap-2">
                  {tags.map(tag => {
                    const active = selected.includes(tag.key)
                    return (
                      <button
                        key={tag.key}
                        type="button"
                        onClick={() => toggle(tag.key)}
                        className="flex items-center gap-1.5 min-h-[40px] px-3 rounded-full text-sm border transition-all whitespace-nowrap active:scale-95"
                        style={chipStyle(active)}
                      >
                        <span className="text-base leading-none">{tag.emoji}</span>
                        <span>{tagLabel(tag)}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
